import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { CreateUserModal } from '../components/CreateUserModal';
import { EditUserModal } from '../components/EditUserModal';
import { UserListItem } from '../services/userService';
import { User } from '../mockData/mockData';
import { UserService } from '../services/userService';

export const UserManagementPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load users when component mounts
  React.useEffect(() => {
    const loadUsers = async () => {
      try {
        setIsLoading(true);
        const fetchedUsers = await UserService.getUsers();
        // Convert API response to User type
        const convertedUsers: User[] = fetchedUsers.map((apiUser: any) => ({
          id: apiUser.id,
          firstName: apiUser.first_name || '-',
          lastName: apiUser.last_name || '',
          email: apiUser.email,
          username: apiUser.email.split('@')[0],
          role: apiUser.role as User['role'],
          isActive: apiUser.status === 'active',
          lastLogin: apiUser.last_login || undefined,
        }));
        setUsers(convertedUsers);
      } catch (error) {
        console.error('Failed to load users:', error);
        // Continue with empty list on error
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' ||
                         (user.firstName?.toLowerCase() ?? '').includes(searchLower) ||
                         (user.lastName?.toLowerCase() ?? '').includes(searchLower) ||
                         (user.email?.toLowerCase() ?? '').includes(searchLower) ||
                         (user.username?.toLowerCase() ?? '').includes(searchLower);

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && user.isActive) ||
                         (statusFilter === 'inactive' && !user.isActive);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadgeClass = (role: User['role']) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'badge badge-admin';
      case 'clinician': return 'badge badge-clinician';
      case 'reception':
      case 'receptionist': return 'badge badge-reception';
      default: return 'badge';
    }
  };

  const getStatusBadgeClass = (isActive: boolean) => {
    return isActive ? 'badge badge-success' : 'badge badge-inactive';
  };

  const handleEditUser = (user: User) => {
    // Convert User to UserListItem format
    const userListItem: UserListItem = {
      id: user.id,
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      display_name: `${user.firstName} ${user.lastName}`,
      role: user.role.toLowerCase(),
      status: user.isActive ? 'active' : 'inactive',
      created_at: '',
    };
    setEditingUser(userListItem);
    setShowEditModal(true);
  };

  const handleEditUserSuccess = (updatedUser: UserListItem) => {
    // Show success message
    const displayName = updatedUser.first_name && updatedUser.last_name 
      ? `${updatedUser.first_name} ${updatedUser.last_name}` 
      : updatedUser.email;
    setSuccessMessage(`Successfully updated user: ${displayName}`);
    
    // Reload users to get updated data
    const loadUsers = async () => {
      try {
        const fetchedUsers = await UserService.getUsers();
        const convertedUsers: User[] = fetchedUsers.map((apiUser: any) => ({
          id: apiUser.id,
          firstName: apiUser.first_name,
          lastName: apiUser.last_name,
          email: apiUser.email,
          username: apiUser.email.split('@')[0],
          role: apiUser.role as User['role'],
          isActive: apiUser.status === 'active',
          lastLogin: apiUser.last_login || undefined,
        }));
        setUsers(convertedUsers);
      } catch (error) {
        console.error('Failed to reload users:', error);
      }
    };
    
    loadUsers();
    
    // Close modal
    setShowEditModal(false);
    setEditingUser(null);
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };

  return (
    <Layout title="User Management">
      <section className="user-management">
        <div className="page-header">
          <h3>User Management</h3>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Add New User
          </button>
        </div>

        {successMessage && (
          <div className="alert alert-success" style={{ marginBottom: '20px' }}>
            ✓ {successMessage}
          </div>
        )}

        <div className="filters">
          <div className="filter-group user-management-filter">
            <label htmlFor="search">Search Users</label>
            <input
              type="text"
              id="search"
              placeholder="Search by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-group user-management-filter">
            <label htmlFor="role-filter">Role</label>
            <select
              id="role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="Admin">Admin</option>
              <option value="Clinician">Clinician</option>
              <option value="Reception">Reception</option>
            </select>
          </div>
          
          <div className="filter-group user-management-filter">
            <label htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="users-table">
          {isLoading ? (
            <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading users...</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td>
                      {user.firstName === '-' ? (
                        <span style={{ color: '#999', fontStyle: 'italic' }} title="Pre-registered - name will be populated on first login">
                          {user.firstName} {user.lastName}
                        </span>
                      ) : (
                        <span>{user.firstName} {user.lastName}</span>
                      )}
                    </td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={getRoleBadgeClass(user.role)}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(user.isActive)}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </button>
                        <button className="btn btn-sm btn-danger">
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!isLoading && filteredUsers.length === 0 && (
          <div className="empty-state">
            <p>{users.length === 0 ? 'No users found. Create one to get started.' : 'No users found matching your criteria.'}</p>
          </div>
        )}

        {/* Create User Modal - Instructions Only */}
        <CreateUserModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        />

        {/* Edit User Modal */}
        <EditUserModal
          isOpen={showEditModal}
          user={editingUser}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          onSuccess={handleEditUserSuccess}
        />
      </section>
    </Layout>
  );
};



